// Zone de signature au doigt (événements pointeur : doigt, stylet ou souris). Le tracé est gardé en VECTEURS (traits de points,
// en pixels du cadre), jamais en image : le serveur le contrôle, le fige dans l'empreinte et le redessine dans le PDF.
import { useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import type { TraceSignature } from '@shared/bons-intervention.ts';
import { Bouton } from '../../components/ui.tsx';

export function ZoneSignature({ libelle, valeur, changer }: { libelle: string; valeur: TraceSignature | null; changer: (t: TraceSignature | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const traits = useRef<[number, number][][]>(valeur?.traits ?? []);
  const enCours = useRef<[number, number][] | null>(null);
  const [taille, setTaille] = useState({ l: 300, h: 140 });

  // Le cadre suit la largeur disponible (téléphone ou écran large), hauteur fixe ; le tracé est relevé dans ce repère.
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const mesurer = () => {
      const l = Math.max(200, Math.round(c.parentElement!.getBoundingClientRect().width));
      setTaille((t) => (t.l === l ? t : { l, h: 140 }));
    };
    mesurer();
    const ro = new ResizeObserver(mesurer);
    ro.observe(c.parentElement!);
    return () => ro.disconnect();
  }, []);

  const dessiner = () => {
    const c = ref.current;
    if (!c) return;
    const r = window.devicePixelRatio || 1;
    c.width = taille.l * r; c.height = taille.h * r;
    const g = c.getContext('2d')!;
    g.scale(r, r);
    g.clearRect(0, 0, taille.l, taille.h);
    g.lineWidth = 2.2; g.lineCap = 'round'; g.lineJoin = 'round';
    g.strokeStyle = getComputedStyle(c).color || '#111827';
    for (const tr of [...traits.current, ...(enCours.current ? [enCours.current] : [])]) {
      g.beginPath();
      tr.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
      if (tr.length === 1) g.lineTo(tr[0][0] + 0.5, tr[0][1]);
      g.stroke();
    }
  };
  useEffect(() => {
    // Changement de largeur : le tracé déjà fait est effacé (il ne correspondrait plus au cadre).
    if (traits.current.length && valeur && valeur.largeur !== taille.l) { traits.current = []; changer(null); }
    dessiner();
  }, [taille]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const b = e.currentTarget.getBoundingClientRect();
    return [Math.round((e.clientX - b.left) * 10) / 10, Math.round((e.clientY - b.top) * 10) / 10];
  };
  const fin = () => {
    if (!enCours.current) return;
    traits.current = [...traits.current, enCours.current];
    enCours.current = null;
    changer({ largeur: taille.l, hauteur: taille.h, traits: traits.current });
    dessiner();
  };

  return (
    <div className="bi-signature">
      <div className="bi-signature-entete">
        <span className="champ-libelle">{libelle}</span>
        <Bouton compact variante="fantome" icone={<Eraser aria-hidden />} onClick={() => { traits.current = []; enCours.current = null; changer(null); dessiner(); }}
          disabled={!valeur}>Effacer</Bouton>
      </div>
      <canvas ref={ref} className="bi-signature-cadre" style={{ width: taille.l, height: taille.h }} role="img"
        aria-label={`${libelle} : signez dans ce cadre avec le doigt, un stylet ou la souris${valeur ? ' (signature tracée)' : ''}`}
        onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); enCours.current = [point(e)]; dessiner(); }}
        onPointerMove={(e) => { if (!enCours.current) return; enCours.current.push(point(e)); dessiner(); }}
        onPointerUp={fin} onPointerCancel={fin} />
      <span className="champ-aide">{valeur ? 'Signature tracée.' : 'Signez dans le cadre.'}</span>
    </div>
  );
}

/** Rendu d'une signature figée (SVG, mêmes vecteurs que le PDF). */
export function SignatureFigee({ trace, libelle }: { trace: TraceSignature; libelle: string }) {
  return (
    <svg className="bi-signature-figee" viewBox={`0 0 ${trace.largeur} ${trace.hauteur}`} role="img" aria-label={libelle} preserveAspectRatio="xMidYMid meet">
      {trace.traits.map((tr, i) => (
        <polyline key={i} points={tr.map(([x, y]) => `${x},${y}`).join(' ')} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}
